/**
 * Pure, in-browser isolation-run engine for an equipment's declared FSM
 * (`operating_modes`). It mirrors what the PLATFORM's predicate runtime does on
 * the wire: raw signals are driven (a coil toggled, an analog ramped past its
 * level, an event fired), and the evaluator turns those signals into transitions
 * and steps `equipment_state` — the sim NEVER asserts state, exactly as a real
 * gateway never does. This is a faithful PREVIEW for authoring, not the
 * authority: the real firing happens server-side once the run goes over the
 * broker.
 *
 * Faithfulness + limits: first-true-guard-wins in declaration order; coil edges
 * (0.5 threshold); analog cross with deadband hysteresis + min-dwell; count /
 * operator / instrument events fire by button; elapsed_since fires off the
 * clock. The one deliberate simplification vs the server is that count/human
 * predicates fire on an explicit button rather than a real pulse train.
 *
 * Everything is a pure function returning a fresh RunState so the component can
 * hold it in useState and the logic stays unit-testable.
 */

import type { ModeTransition, OperatingModes } from "./operating-modes";

/** A drivable on/off signal (a coil / discrete). `isEquals` marks a coil_equals target. */
export interface CoilSignal {
  key: string;
  isEquals: boolean;
}

/** A drivable continuous signal (an analog), with a slider range. */
export interface AnalogSignal {
  key: string;
  min: number;
  max: number;
  unit?: string;
}

/** An event a human / counter fires directly (count_pulse, operator_push, …). */
export interface EventSignal {
  on_event: string;
  predicate: string;
}

/** The static descriptors of everything drivable in a model, for rendering controls. */
export interface RunControls {
  coils: CoilSignal[];
  analogs: AnalogSignal[];
  events: EventSignal[];
}

/** One accumulated state band on the run timeline (open while `t1` is undefined). */
export interface RunSegment {
  mode: string;
  t0: number;
  t1?: number;
}

/** One fired transition, newest first in the log. */
export interface RunLogEntry {
  t: number;
  from: string;
  on_event: string;
  to: string;
}

/** The full runtime state of an isolation run. */
export interface RunState {
  t: number;
  mode: string;
  coils: Record<string, number>;
  prevCoils: Record<string, number>;
  analogs: Record<string, number>;
  prevAnalogs: Record<string, number>;
  /** Per-transition (by index) deadband latch: true once fired, until the signal falls back. */
  armed: Record<string, boolean>;
  /** Per-transition (by index) time the analog first went above its level (for min-dwell). */
  aboveSince: Record<string, number>;
  /** on_event -> t of last fire (the elapsed_since anchor). */
  anchors: Record<string, number>;
  /** Events fired by button since the last step. */
  pendingEvents: string[];
  segments: RunSegment[];
  log: RunLogEntry[];
}

/** Optional per-parameter range lookup, so analog sliders match the real tag range. */
export type ParamRange = (
  param: string,
) => { min?: number; max?: number; unit?: string; seed?: number } | undefined;

/** The measurement param a tag_hint names is its suffix (`induction_furnace.power_kw` -> `power_kw`). */
function paramOf(tagHint: string): string {
  const dot = tagHint.lastIndexOf(".");
  return dot >= 0 ? tagHint.slice(dot + 1) : tagHint;
}

/** Collect the drivable signals a model's transitions reference, for the control panel. */
export function collectControls(om: OperatingModes, paramRange?: ParamRange): RunControls {
  const coils = new Map<string, CoilSignal>();
  const analogs = new Map<string, AnalogSignal>();
  const events: EventSignal[] = [];
  const seenEvents = new Set<string>();

  for (const t of om.transitions ?? []) {
    const g = t.trigger;
    const p = g?.predicate;
    if (!p) continue;
    if (p.startsWith("coil")) {
      if (g?.tag_hint && !coils.has(g.tag_hint)) {
        coils.set(g.tag_hint, { key: g.tag_hint, isEquals: p === "coil_equals" });
      }
    } else if (p.startsWith("analog_cross")) {
      if (g?.tag_hint && !analogs.has(g.tag_hint)) {
        const rng = paramRange?.(paramOf(g.tag_hint));
        const lv = g.level ?? 0;
        const min = rng?.min ?? (lv ? Math.min(0, lv * 0.5) : 0);
        const max = rng?.max ?? (lv ? lv * 1.5 : 100);
        analogs.set(g.tag_hint, { key: g.tag_hint, min, max, unit: rng?.unit });
      }
    } else if (
      p === "count_pulse" ||
      p === "count_delta" ||
      p === "operator_push" ||
      p === "instrument_push"
    ) {
      if (!seenEvents.has(t.on_event)) {
        seenEvents.add(t.on_event);
        events.push({ on_event: t.on_event, predicate: p });
      }
    }
    // elapsed_since is clock-driven: no control.
  }
  return { coils: [...coils.values()], analogs: [...analogs.values()], events };
}

function initialMode(om: OperatingModes): string {
  if (om.current_mode && (om.modes ?? []).includes(om.current_mode)) return om.current_mode;
  return om.modes?.[0] ?? "";
}

/** Seed a fresh run at the declared start mode, all signals at rest / range floor. */
export function createRun(om: OperatingModes, controls: RunControls, paramRange?: ParamRange): RunState {
  const coils: Record<string, number> = {};
  for (const c of controls.coils) coils[c.key] = 0;
  const analogs: Record<string, number> = {};
  for (const a of controls.analogs) {
    const seed = paramRange?.(paramOf(a.key))?.seed;
    analogs[a.key] = seed ?? a.min;
  }
  const start = initialMode(om);
  return {
    t: 0,
    mode: start,
    coils,
    prevCoils: { ...coils },
    analogs,
    prevAnalogs: { ...analogs },
    armed: {},
    aboveSince: {},
    anchors: {},
    pendingEvents: [],
    segments: start ? [{ mode: start, t0: 0 }] : [],
    log: [],
  };
}

export function setCoil(state: RunState, key: string, value: number): RunState {
  return { ...state, coils: { ...state.coils, [key]: value } };
}

export function setAnalog(state: RunState, key: string, value: number): RunState {
  return { ...state, analogs: { ...state.analogs, [key]: value } };
}

export function fireEvent(state: RunState, onEvent: string): RunState {
  return { ...state, pendingEvents: [...state.pendingEvents, onEvent] };
}

function firesEdge(prev: number, now: number, rising: boolean): boolean {
  return rising ? prev < 0.5 && now >= 0.5 : prev >= 0.5 && now < 0.5;
}

function dwellSeconds(t: ModeTransition): number {
  const ms = t.trigger?.min_dwell_ms ?? t.trigger?.dwell_ms ?? 0;
  return ms / 1000;
}

/**
 * Does transition `t` (index `key`) fire given the current + previous signal
 * values? Reads arming/anchor latches already advanced for this step.
 */
function evalTransition(state: RunState, t: ModeTransition, key: string): boolean {
  const g = t.trigger;
  const p = g?.predicate;
  if (!p) return false;
  const s = g?.tag_hint ?? "";

  switch (p) {
    case "coil_rising":
      return firesEdge(state.prevCoils[s] ?? 0, state.coils[s] ?? 0, true);
    case "coil_falling":
      return firesEdge(state.prevCoils[s] ?? 0, state.coils[s] ?? 0, false);
    case "coil_equals":
      return String(state.coils[s] ?? 0) === String(g?.value ?? "");
    case "count_pulse":
    case "count_delta":
    case "operator_push":
    case "instrument_push":
      return state.pendingEvents.includes(t.on_event);
    case "analog_cross_above": {
      const a = state.analogs[s];
      if (a === undefined) return false;
      const lv = g?.level ?? 0;
      const since = state.aboveSince[key];
      return a >= lv && !state.armed[key] && since !== undefined && state.t - since >= dwellSeconds(t);
    }
    case "analog_cross_below": {
      const a = state.analogs[s];
      if (a === undefined) return false;
      const lv = g?.level ?? 0;
      return (state.prevAnalogs[s] ?? a) > lv && a <= lv;
    }
    case "elapsed_since": {
      const anchor = g?.after_event ? state.anchors[g.after_event] : undefined;
      if (anchor === undefined) return false;
      return state.t - anchor >= (g?.duration_ms ?? 0) / 1000;
    }
    default:
      return false;
  }
}

/**
 * Advance the run by `dt` seconds: refresh analog arming, fire the first eligible
 * transition out of the current mode (declaration order), append the segment +
 * log, then roll prev-values forward and clear the one-shot event queue.
 */
export function stepRun(state: RunState, om: OperatingModes, dt: number): RunState {
  const t = state.t + dt;
  const transitions = om.transitions ?? [];

  // Advance analog arming (min-dwell timer + deadband latch reset) before eval.
  const armed = { ...state.armed };
  const aboveSince = { ...state.aboveSince };
  transitions.forEach((tr, i) => {
    if (tr.trigger?.predicate !== "analog_cross_above") return;
    const s = tr.trigger.tag_hint ?? "";
    const a = state.analogs[s];
    if (a === undefined) return;
    const key = String(i);
    const lv = tr.trigger.level ?? 0;
    const db = tr.trigger.deadband ?? 0;
    if (a >= lv) {
      if (aboveSince[key] === undefined) aboveSince[key] = t;
    } else {
      delete aboveSince[key];
    }
    if (a < lv - db) armed[key] = false;
  });

  const evalState: RunState = { ...state, t, armed, aboveSince };

  // First-true-guard-wins, in declaration order, among transitions out of the current mode.
  let mode = state.mode;
  const segments = state.segments.map((s) => ({ ...s }));
  const anchors = { ...state.anchors };
  let log = state.log;

  for (let i = 0; i < transitions.length; i++) {
    const tr = transitions[i];
    const trFrom = tr.from ?? [];
    if (trFrom.length > 0 && !trFrom.includes(mode)) continue;
    if (evalTransition(evalState, tr, String(i))) {
      if (tr.trigger?.predicate === "analog_cross_above") armed[String(i)] = true;
      anchors[tr.on_event] = t;
      if (segments.length) segments[segments.length - 1].t1 = t;
      segments.push({ mode: tr.to, t0: t });
      log = [{ t, from: mode, on_event: tr.on_event, to: tr.to }, ...log];
      mode = tr.to;
      break;
    }
  }

  // Non-mode events fired this step still anchor elapsed_since chains.
  for (const ev of state.pendingEvents) anchors[ev] = t;

  return {
    ...state,
    t,
    mode,
    prevCoils: { ...state.coils },
    prevAnalogs: { ...state.analogs },
    armed,
    aboveSince,
    anchors,
    pendingEvents: [],
    segments,
    log,
  };
}
